import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirm } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import { sortBy, without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, SET_GENERIC } from 'store/actions';
import { selectGeneric } from 'store/app/selectors';
import { doPlansDeletePlan, doPlansUpdatePlan } from 'store/plan/actions';
import {
  selectPlansPlan,
  selectPlansPlanPins,
  selectPlansEditable,
} from 'store/plan/selectors';
import {
  GENERIC_KEY_PLAN_PIN_GROUP_HIDDEN,
  PinNewWithName,
} from 'store/plan/types';

export const CanvasInfo: React.FC<
  ModalScreenProps<ModalScreens.PLANS>
> = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const plansEditable = useSelector(selectPlansEditable);
  const plan = useSelector(selectPlansPlan)!;
  const pins = useSelector(selectPlansPlanPins);

  const handleChangePlanName = React.useCallback(
    (name: string) => dispatch(doPlansUpdatePlan({ name })),
    [dispatch],
  );

  const handleDeletePlan = () =>
    confirm({
      confirmText: 'Delete',
      destructive: true,
      onConfirm: () => dispatch(doPlansDeletePlan({ id: plan.id })),
      text: 'Are you sure you want to delete this plan?',
    });

  const hiddenPinGroups: string[] =
    (useSelector((state) =>
      selectGeneric(state, GENERIC_KEY_PLAN_PIN_GROUP_HIDDEN),
    ) as string[] | undefined) || [];

  const handleTogglePinGroupVisibility = (group: string) => {
    const newHiddenPinGroups = hiddenPinGroups.includes(group)
      ? without(hiddenPinGroups, group)
      : [...hiddenPinGroups, group];
    dispatch(
      SET_GENERIC({
        key: GENERIC_KEY_PLAN_PIN_GROUP_HIDDEN,
        value: newHiddenPinGroups.length ? newHiddenPinGroups : undefined,
      }),
    );
  };

  const pinByGroup = sortBy(pins, ['group', 'name']).reduce<
    Record<string, PinNewWithName[]>
  >((acc, pin) => {
    const group = pin.group || 'Other Pins';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group]!.push(pin);
    return acc;
  }, {});

  useEsc(() => dispatch(NAVIGATE_MODAL_BACK()));

  return (
    <PageScrollView
      header={
        <NavigationHeader
          onPressCancelButton={() => dispatch(NAVIGATE_MODAL_BACK())}
          cancelButtonText="Close"
          heading="Plan Info"
        />
      }
    >
      {plan.image.isFileMissing && (
        <Card bodyStyle={{ borderColor: theme.colors.yellow, borderWidth: 1 }}>
          <CardRow
            icon={ICONS.CircleExclamationLight}
            value="This plan's file is missing. Trying closing plans and syncing"
          />
        </Card>
      )}
      {plan.image.isUsingThumbnail && (
        <Card bodyStyle={{ borderColor: theme.colors.yellow, borderWidth: 1 }}>
          <CardRow
            icon={ICONS.CircleInfoLight}
            value="You're viewing a low resolution version of this plan. To view a higher resolution version, close the plan and sync"
          />
        </Card>
      )}
      {plansEditable ? (
        <Card
          label="Plan Name"
          explanation={
            plan.pinCount
              ? 'This plan cannot be deleted as pins have been linked to it'
              : ''
          }
        >
          <TextField
            value={plan.name}
            onChange={handleChangePlanName}
            placeholder="Plan Name"
            autoFocusWeb
            autoFocusDelay
            onEsc={() => dispatch(NAVIGATE_MODAL_BACK())}
          />
        </Card>
      ) : (
        <Card label="Plan Name">
          <CardRow value={plan.name} />
        </Card>
      )}
      {Object.entries(pinByGroup).map(([group, groupPins]) => {
        const hideGroup = hiddenPinGroups.includes(group);
        return (
          <Card
            key={group}
            label={group}
            labelLinkText={hideGroup ? 'Show On Plan' : 'Hide On Plan'}
            onLabelLinkPress={() => handleTogglePinGroupVisibility(group)}
          >
            {hideGroup ? (
              <CardRow
                value={pluralise(
                  groupPins.length,
                  '1 hidden pin',
                  '{x} hidden pins',
                )}
              />
            ) : (
              groupPins.map((pin) => (
                <CardRow
                  key={pin.id}
                  label={pin.name}
                  icon={ICONS.LocationPinSolidStroke}
                  iconColor={pin.color}
                />
              ))
            )}
          </Card>
        );
      })}
      {!plan.pinCount && plansEditable && (
        <Button
          text="Delete Plan"
          onPress={handleDeletePlan}
          buttonTheme={ButtonTheme.themeless}
          destructive
          style={{ alignSelf: 'center' }}
        />
      )}
    </PageScrollView>
  );
};
